
import React from "react";


// ========================================
// PRODUCT APPLICATIONS
// ========================================

const ProductApplications = ({
    data = [],
    onChange
}) => {

    const applications = Array.isArray(data)
        ? data
        : [];


    // ========================================
    // STYLES
    // ========================================

    const inputClass =
        "w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-300";

    const labelClass =
        "block text-sm font-medium text-gray-700 mb-2";

    const addButtonClass =
        "px-4 py-2 bg-gray-800 text-white text-sm rounded hover:bg-gray-700";

    const removeButtonClass =
        "px-3 py-2 bg-red-50 text-red-700 border border-red-200 text-sm rounded hover:bg-red-100";


    // ========================================
    // APPLICATION MANAGEMENT
    // ========================================

    const addApplication = () => {

        onChange([
            ...applications,
            {
                name: "",
                description: "",
                industries: [],
                surfaces: [],
                keywords: []
            }
        ]);

    };


    const removeApplication = (index) => {

        onChange(
            applications.filter(
                (_, i) => i !== index
            )
        );

    };


    const updateApplication = (
        index,
        field,
        value
    ) => {

        onChange(
            applications.map(
                (application, i) =>
                    i === index
                        ? {
                            ...application,
                            [field]: value
                        }
                        : application
            )
        );

    };


    // ========================================
    // REPEATABLE LIST MANAGEMENT
    // ========================================

    const addListItem = (
        applicationIndex,
        field
    ) => {

        const application =
            applications[applicationIndex];

        updateApplication(
            applicationIndex,
            field,
            [
                ...(application[field] || []),
                ""
            ]
        );

    };


    const updateListItem = (
        applicationIndex,
        field,
        itemIndex,
        value
    ) => {

        const items =
            applications[applicationIndex][field] || [];

        updateApplication(
            applicationIndex,
            field,
            items.map(
                (item, index) =>
                    index === itemIndex
                        ? value
                        : item
            )
        );

    };


    const removeListItem = (
        applicationIndex,
        field,
        itemIndex
    ) => {

        const items =
            applications[applicationIndex][field] || [];

        updateApplication(
            applicationIndex,
            field,
            items.filter(
                (_, index) => index !== itemIndex
            )
        );

    };


    // ========================================
    // REUSABLE LIST EDITOR
    // ========================================

    const renderListEditor = (
        application,
        applicationIndex,
        field,
        title,
        placeholder
    ) => {

        const items = Array.isArray(application[field])
            ? application[field]
            : [];

        return (

            <div className="space-y-3">

                <div className="flex justify-between items-center gap-3">

                    <label className={labelClass}>
                        {title}
                    </label>

                    <button
                        type="button"
                        onClick={() =>
                            addListItem(
                                applicationIndex,
                                field
                            )
                        }
                        className="text-sm text-blue-700 hover:underline"
                    >
                        + Add
                    </button>

                </div>


                {items.length === 0 && (

                    <p className="text-xs text-gray-500">
                        No entries added.
                    </p>

                )}


                {items.map((item, itemIndex) => (

                    <div
                        key={itemIndex}
                        className="flex gap-2"
                    >

                        <input
                            type="text"
                            value={item || ""}
                            onChange={(event) =>
                                updateListItem(
                                    applicationIndex,
                                    field,
                                    itemIndex,
                                    event.target.value
                                )
                            }
                            placeholder={placeholder}
                            className={inputClass}
                        />

                        <button
                            type="button"
                            onClick={() =>
                                removeListItem(
                                    applicationIndex,
                                    field,
                                    itemIndex
                                )
                            }
                            className={removeButtonClass}
                        >
                            Remove
                        </button>

                    </div>

                ))}

            </div>

        );

    };


    // ========================================
    // RENDER
    // ========================================

    return (

        <div className="space-y-6">

            {/* HEADER */}

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 border-b pb-4">

                <div>

                    <h3 className="text-base font-semibold text-gray-900">
                        Product Applications
                    </h3>

                    <p className="text-sm text-gray-500 mt-1">
                        Define where and how this product can be
                        used, including relevant industries,
                        surfaces and search keywords.
                    </p>

                </div>

                <button
                    type="button"
                    onClick={addApplication}
                    className={addButtonClass}
                >
                    + Add Application
                </button>

            </div>


            {/* EMPTY STATE */}

            {applications.length === 0 && (

                <div className="py-12 text-center border border-dashed rounded-lg">

                    <p className="text-sm text-gray-500">
                        No product applications added yet.
                    </p>

                    <p className="text-xs text-gray-400 mt-2">
                        Add the applications approved for this product.
                    </p>

                </div>

            )}


            {/* APPLICATIONS */}

            {applications.map(
                (application, applicationIndex) => (

                    <div
                        key={applicationIndex}
                        className="border border-gray-200 rounded-lg overflow-hidden"
                    >

                        {/* APPLICATION HEADER */}

                        <div className="flex justify-between items-center gap-3 bg-gray-100 px-5 py-4">

                            <div>

                                <h4 className="font-semibold text-gray-900">

                                    Application {applicationIndex + 1}

                                </h4>

                                {application.name && (

                                    <p className="text-xs text-gray-500 mt-1">
                                        {application.name}
                                    </p>

                                )}

                            </div>

                            <button
                                type="button"
                                onClick={() =>
                                    removeApplication(
                                        applicationIndex
                                    )
                                }
                                className={removeButtonClass}
                            >
                                Remove Application
                            </button>

                        </div>


                        {/* APPLICATION FIELDS */}

                        <div className="p-5 space-y-7">

                            {/* NAME */}

                            <div>

                                <label className={labelClass}>
                                    Application Name
                                </label>

                                <input
                                    type="text"
                                    value={application.name || ""}
                                    onChange={(event) =>
                                        updateApplication(
                                            applicationIndex,
                                            "name",
                                            event.target.value
                                        )
                                    }
                                    placeholder="e.g. Roof Waterproofing"
                                    className={inputClass}
                                />

                            </div>


                            {/* DESCRIPTION */}

                            <div>

                                <label className={labelClass}>
                                    Application Description
                                </label>

                                <textarea
                                    value={application.description || ""}
                                    onChange={(event) =>
                                        updateApplication(
                                            applicationIndex,
                                            "description",
                                            event.target.value
                                        )
                                    }
                                    placeholder="Describe how this product is used for this application."
                                    rows={4}
                                    className={inputClass}
                                />

                            </div>


                            {/* INDUSTRIES */}

                            {renderListEditor(
                                application,
                                applicationIndex,
                                "industries",
                                "Industries",
                                "e.g. Construction"
                            )}


                            {/* SURFACES */}

                            {renderListEditor(
                                application,
                                applicationIndex,
                                "surfaces",
                                "Suitable Surfaces / Substrates",
                                "e.g. Concrete roof slab"
                            )}


                            {/* KEYWORDS */}

                            {renderListEditor(
                                application,
                                applicationIndex,
                                "keywords",
                                "Application Keywords",
                                "e.g. terrace waterproofing"
                            )}

                        </div>

                    </div>

                )
            )}

        </div>

    );

};

export default ProductApplications;
